/**
 * Linked embroidery cart items (Symmetry).
 *
 * A monogrammed product is added as two line items — the product and a
 * "Custom Embroidery" fee — sharing a `_embroidery_group` property. When one
 * is removed, remove every line in the group (port of the original theme's
 * `removeWithLinked` in cart.js).
 *
 * Listens in the capture phase so it wins over Symmetry's cart-form delegate,
 * removes the group's lines bottom-up via /cart/change.js, then dispatches
 * `on:cart:change` so cart-form re-renders.
 */
class EmbroideryCartLinker {
  constructor() {
    this.processing = false;
    document.addEventListener('click', this.handleClick.bind(this), true);
  }

  handleClick(evt) {
    const removeBtn = evt.target.closest('.cart-item__remove');
    if (!removeBtn) return;

    const item = removeBtn.closest('.cart-item');
    const group = item ? item.getAttribute('data-embroidery-group') : null;
    if (!group) return; // not a linked item — normal removal

    evt.preventDefault();
    evt.stopImmediatePropagation();

    if (this.processing) return;
    this.processing = true;

    this.removeGroup(group).finally(() => {
      this.processing = false;
    });
  }

  async removeGroup(group) {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (!cart || !cart.items) return;

      // 1-based line numbers sharing this group, removed bottom-up so
      // indices don't shift.
      const linesToRemove = cart.items
        .map((it, idx) => ({
          idx: idx + 1,
          group: (it.properties && it.properties._embroidery_group) || null
        }))
        .filter((x) => x.group === group)
        .map((x) => x.idx)
        .sort((a, b) => b - a);

      for (const line of linesToRemove) {
        // eslint-disable-next-line no-await-in-loop
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity: 0 })
        });
      }

      // Let Symmetry's cart-form(s) re-render.
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
    } catch (err) {
      console.error('[EmbroideryCartLinker] Error removing linked items:', err);
      window.location.reload();
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new EmbroideryCartLinker());
} else {
  new EmbroideryCartLinker();
}
